import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentCard from "@/components/cre/AgentCard";
import ConsensusPanel from "@/components/cre/ConsensusPanel";
import RunbookPanel from "@/components/cre/RunbookPanel";
import AuditTranscript from "@/components/cre/AuditTranscript";
import ExecutiveSummary from "@/components/cre/ExecutiveSummary";
import GuardrailPanel from "@/components/cre/GuardrailPanel";
import CollapsibleSection from "@/components/cre/CollapsibleSection";
// Specialist lenses deprecated — sub-capabilities live in AppSvc card
import RootCausePanel from "@/components/cre/RootCausePanel";
import FinalActionPlan from "@/components/cre/FinalActionPlan";
import PipelineProgress from "@/components/cre/PipelineProgress";
import { Badge } from "@/components/ui/badge";
import { Eye, ShieldOff, Loader2, Activity, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SEVERITY_COLORS = {
  P1: "border-rose-500/40 text-rose-300 bg-rose-500/5",
  P2: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  P3: "border-sky-500/40 text-sky-300 bg-sky-500/5",
  P4: "border-zinc-500/40 text-zinc-300 bg-zinc-500/5",
};

const STATUS_STYLE = {
  pending_analysis: "text-zinc-400",
  analyzing: "text-blue-300",
  awaiting_approval: "text-amber-300",
  approved: "text-emerald-300",
  rejected: "text-rose-300",
  modified: "text-violet-300",
};

/**
 * Public read-only AI workspace. No header / nav, no approval / archive /
 * feedback / share controls. Live polls every 4s while analyzing so
 * stakeholders watching from Slack see agents reason in real time.
 */
export default function SharedWarRoom() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const res = await api.publicByToken(token);
        if (!cancelled) {
          setData(res);
          setError(null);
        }
        return res;
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "revoked" : "error");
        return null;
      }
    };

    (async () => {
      setLoading(true);
      let res = await fetchOnce();
      setLoading(false);
      while (!cancelled) {
        const status = res?.incident?.status;
        const interval = status === "analyzing" ? 4000 : 15000;
        await new Promise((r) => setTimeout(r, interval));
        if (cancelled) return;
        res = await fetchOnce();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen cre-grid-bg flex items-center justify-center">
        <Loader2 className="h-5 w-5 text-blue-300 animate-spin" />
        <span className="ml-3 text-sm text-zinc-400">Loading shared AI workspace…</span>
      </div>
    );
  }

  if (error === "revoked" || !data) {
    return (
      <div className="min-h-screen cre-grid-bg flex items-center justify-center px-6">
        <div
          data-testid="shared-revoked"
          className="cre-surface rounded-sm p-8 max-w-md text-center"
        >
          <ShieldOff className="h-8 w-8 text-rose-400 mx-auto" strokeWidth={1.5} />
          <div className="mt-4 font-heading text-xl font-bold tracking-tight">
            Link revoked or invalid
          </div>
          <div className="mt-2 text-sm text-zinc-400">
            This shared link is no longer active. Ask the incident owner to
            generate a fresh link from the dashboard.
          </div>
        </div>
      </div>
    );
  }

  const { incident, mttr_report } = data;
  const hasAnalysis = Boolean(incident.consensus && incident.runbook);
  const hasLenses = false;
  const isLive = incident.status === "analyzing";

  return (
    <div className="min-h-screen cre-dot-bg">
      {/* Read-only banner */}
      <div
        data-testid="shared-banner"
        className="sticky top-0 z-40 border-b border-emerald-500/20 bg-emerald-500/[0.06] backdrop-blur"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-emerald-300" strokeWidth={1.75} />
            <span className="cre-overline text-emerald-300">
              Read-only · shared AI workspace
            </span>
            <Badge
              variant="outline"
              className="rounded-sm font-mono text-[10px] border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
            >
              Advisory · human validation required
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-400">
            {isLive ? (
              <>
                <Activity className="h-3 w-3 text-blue-300 animate-pulse" />
                <span className="text-blue-300">live · agents reasoning</span>
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3 text-zinc-400" />
                <span>auto-refresh every 15s</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={`rounded-sm font-mono ${SEVERITY_COLORS[incident.severity] || ""}`}
              >
                {incident.severity}
              </Badge>
              <span className={`cre-overline ${STATUS_STYLE[incident.status] || ""}`}>
                · {incident.status.replace(/_/g, " ")}
              </span>
              <span className="text-[11px] font-mono text-zinc-600">
                · {incident.source}
              </span>
            </div>
            <h1
              data-testid="shared-incident-title"
              className="mt-2 font-heading text-2xl md:text-3xl font-black tracking-tight"
            >
              {incident.title}
            </h1>
            <div className="mt-1 text-sm text-zinc-500 font-mono">
              {incident.affected_system}
            </div>
          </div>
        </div>

        {/* Executive summary */}
        <ExecutiveSummary incident={incident} mttrOverride={mttr_report} readOnly />

        {/* Pipeline progress strip — public watchers see exactly which
            stage is in flight while agents reason. */}
        {incident.pipeline_progress?.length ? (
          <PipelineProgress stages={incident.pipeline_progress} />
        ) : null}

        {/* Structured root cause analysis */}
        {incident.root_cause_analysis ? (
          <CollapsibleSection
            overline="Root cause"
            title="Likely failure mechanism — structured analysis"
            testId="section-rootcause"
            defaultOpen={true}
          >
            <RootCausePanel rootCause={incident.root_cause_analysis} />
          </CollapsibleSection>
        ) : null}

        {/* Incident summary */}
        <CollapsibleSection
          overline="Incident"
          title="Incident summary"
          testId="section-incident"
          defaultOpen={true}
        >
          <div className="cre-surface rounded-sm p-5 md:p-6">
            <p className="text-sm text-zinc-300 leading-relaxed">
              {incident.description}
            </p>
            {incident.symptoms?.length ? (
              <div className="mt-4">
                <div className="cre-overline mb-1.5">Symptoms</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {incident.symptoms.map((s, i) => (
                    <div
                      key={i}
                      className="text-xs text-zinc-400 font-mono flex gap-2"
                    >
                      <span className="text-blue-400">›</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {incident.telemetry_hints ? (
              <div className="mt-4">
                <div className="cre-overline mb-1.5">Telemetry hints</div>
                <div className="cre-terminal rounded-sm p-3 text-xs">
                  {incident.telemetry_hints}
                </div>
              </div>
            ) : null}
          </div>
        </CollapsibleSection>

        {isLive && !hasAnalysis ? (
          <div className="cre-surface rounded-sm p-10 text-center">
            <Loader2 className="h-6 w-6 mx-auto text-blue-300 animate-spin" />
            <div className="mt-3 font-heading font-semibold">
              Domain agents + safety guardrail are reasoning in parallel…
            </div>
            <div className="mt-1 text-xs text-zinc-500 font-mono">
              this view will update automatically
            </div>
          </div>
        ) : null}

        {incident.hypotheses?.length ? (
          <CollapsibleSection
            overline="Agent workspace"
            title="Domain agent hypotheses"
            testId="section-agents"
          >
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="agent-grid"
            >
              {incident.hypotheses.map((h, i) => (
                <AgentCard key={h.agent_id} hyp={h} index={i} />
              ))}
            </div>
          </CollapsibleSection>
        ) : null}

        {hasAnalysis ? (
          <CollapsibleSection
            overline="Consensus"
            title="Weighted consensus & safety review"
            testId="section-consensus"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ConsensusPanel consensus={incident.consensus} />
              <GuardrailPanel review={incident.guardrail} />
            </div>
          </CollapsibleSection>
        ) : null}

        {hasAnalysis && incident.final_action_plan ? (
          <CollapsibleSection
            overline="Final plan"
            title="Final Recommended Action Plan"
            testId="section-final-plan"
            defaultOpen={true}
          >
            <FinalActionPlan plan={incident.final_action_plan} />
          </CollapsibleSection>
        ) : null}

        {hasAnalysis ? (
          <CollapsibleSection
            overline="Runbook"
            title="Generated remediation runbook"
            testId="section-runbook"
          >
            <RunbookPanel
              runbook={incident.runbook}
              acks={incident.runbook_step_acks}
              readOnly
            />
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          overline="Audit"
          title="Audit transcript"
          testId="section-audit"
        >
          <AuditTranscript events={incident.audit_trail || []} />
        </CollapsibleSection>

        <div className="text-center text-[11px] font-mono text-zinc-600 pt-4 pb-8">
          CRE · Collaborative Resolution Environment · advisory only ·
          read-only stakeholder view
        </div>
      </div>
    </div>
  );
}
